import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, OnDestroy, signal } from '@angular/core';
import { AbstractControl, TouchedChangeEvent, PristineChangeEvent, StatusChangeEvent, ValueChangeEvent } from '@angular/forms';
import { Subscription } from 'rxjs';
import { NeoFormFieldModule } from '@neogov/pattern-library/neo-form-field';
import { FieldErrorData } from './field-error-data';

export interface FieldConstraints {
  maxlength?: number | null;
  minlength?: number | null;
  min?: number | null;
  max?: number | null;
}

/**
 * Reusable component for displaying form field validation errors.
 *
 * Automatically displays error messages based on form control state,
 * with support for custom messages and standard Angular validators.
 *
 * @example
 * ```html
 * <cm-field-errors
 *   [control]="emailControl"
 *   label="Email Address"
 *   [customMessages]="{ required: 'Email is required' }"
 * />
 * ```
 */
@Component({
  selector: 'cm-field-errors',
  standalone: true,
  imports: [NeoFormFieldModule],
  providers: [FieldErrorData],
  templateUrl: './field-errors.component.html',
  styleUrl: './field-errors.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldErrorsComponent implements OnDestroy {
  private readonly _errorService = inject(FieldErrorData);
  private readonly _destroyRef = inject(DestroyRef);
  private _subscriptions: Subscription[] = [];

  /**
   * The form control to check for validation errors.
   * Can be null if control is not yet initialized.
   */
  public readonly control = input.required<AbstractControl | null>();

  /**
   * The field label to use in error messages.
   */
  public readonly label = input.required<string>();

  /**
   * Internal signal to track control state changes.
   * Updated via subscriptions to control's statusChanges and valueChanges.
   */
  private readonly _controlState = signal<{
    errors: Record<string, unknown> | null;
    touched: boolean;
    dirty: boolean;
  } | null>(null);

  /**
   * Custom error messages by validator key.
   * Overrides default messages from FieldErrorMessageService.
   *
   * @example
   * ```typescript
   * { required: 'This field is mandatory', email: 'Enter a valid email' }
   * ```
   */
  public readonly customMessages = input<Partial<Record<string, string>>>({});

  /**
   * When to display error messages.
   * - 'touched': Show after field is touched (default)
   * - 'dirty': Show after field is modified
   * - 'always': Always show if errors exist
   */
  public readonly showErrorsWhen = input<'touched' | 'dirty' | 'always'>('touched');

  /**
   * Force hide errors even if control has errors.
   * Useful for conditional error display logic.
   */
  public readonly hideErrors = input<boolean>(false);

  /**
   * Field constraints for generating default error messages.
   * Used by FieldErrorMessageService to create contextual messages.
   */
  public readonly constraints = input<FieldConstraints>({});

  constructor() {
    // Set up subscriptions to control state changes
    // Use effect to react when the control input changes
    effect(() => {
      const control = this.control();

      // Clean up previous subscriptions
      this._subscriptions.forEach(sub => sub.unsubscribe());
      this._subscriptions = [];

      if (control) {
        // Subscribe to ALL control state changes via events observable
        // This captures touched, dirty, status, and value changes
        // No setTimeout needed - events emit synchronously when state changes
        const eventsSub = control.events?.subscribe(event => {
          // Update state for relevant event types
          if (
            event instanceof TouchedChangeEvent ||
            event instanceof PristineChangeEvent ||
            event instanceof StatusChangeEvent ||
            event instanceof ValueChangeEvent
          ) {
            this._updateControlState();
          }
        });

        if (eventsSub) {
          this._subscriptions.push(eventsSub);
        }

        // Initial state update
        this._updateControlState();
      } else {
        this._controlState.set(null);
      }
    });
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Updates the internal control state signal with current values from the control.
   */
  private _updateControlState(): void {
    const control = this.control();
    if (control) {
      this._controlState.set({
        errors: control.errors,
        touched: control.touched,
        dirty: control.dirty,
      });
    }
  }

  /**
   * Computed signal that determines if errors should be displayed.
   * Based on showErrorsWhen input and control state.
   */
  public readonly shouldShowErrors = computed(() => {
    const controlState = this._controlState();

    if (this.hideErrors() || !controlState) {
      return false;
    }

    const when = this.showErrorsWhen();

    switch (when) {
      case 'always':
        return true;
      case 'dirty':
        return controlState.dirty;
      case 'touched':
      default:
        return controlState.touched;
    }
  });

  /**
   * Computed signal that returns the error message to display.
   * Returns null if no errors or errors should not be shown.
   */
  public readonly errorMessage = computed(() => {
    const controlState = this._controlState();

    if (this.hideErrors() || !controlState) {
      return null;
    }

    const errors = controlState.errors;
    if (!errors) {
      return null;
    }

    return this._errorService.getErrorMessage(
      errors,
      this.label(),
      this.customMessages(),
      this.constraints()
    );
  });
}
